import { useId, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { CircleQuestionMarkIcon } from "lucide-react";

import { isFieldEditableUnderDeadline } from "simplydo/core/ideas";
import { hasPermission } from "simplydo/core/permissions";
import type { OpenAPI } from "simplydo/interfaces";
import { MarkdownViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Divider } from "simplydo/react/ui/divider";
import {
  FormField,
  FormFieldLabelledBy,
  FormLabel,
} from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";
import { TextArea } from "simplydo/react/ui/textarea";

import { useCurrentUser } from "utils/currentUser";

import FieldHeader from "components/ideas/FieldHeader";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { useChildIdea } from "components/ideas/hooks/useIdea";
import type { IdeaExternalChanges } from "components/ideas/types";
import type {
  IdeaCustomFieldIdea,
  IdeaWithIdeaTemplate,
} from "components/lib/Ideas/IdeaPreview/types";

import Assessment from "./Assessment";
import AssessmentFeedback from "./AssessmentFeedback";
import CompanyReport from "./CompanyReport";
import Dropdown from "./Dropdown";
import { FieldProps } from "./FieldProps";
import File from "./File";
import Image from "./Image";
import InfoText from "./InfoText";
import LongText from "./LongText";
import MultiFile from "./MultiFile";
import MultiImage from "./MultiImage";
import NumericalText from "./NumericalText";
import OptionMultiple from "./OptionMultiple";
import RichText from "./RichText";
import ShortText from "./ShortText";
import Slider from "./Slider";
import TableField from "./Table";
import Video from "./Video";
import { fieldVisibilityCheck } from "./fieldCondition";

const BodyContainer = styled(GridColumn)`
  border: none;
`;

type AssessmentSummaryProps = {
  assessmentSummary:
    | NonNullable<
        NonNullable<OpenAPI.Schemas["IndexedIdea"]["summary"]>["assessment"]
      >[string]
    | undefined;
  sectionId: string;
  fieldId: string;
};

const AssessmentSummary = ({
  assessmentSummary,
  sectionId,
  fieldId,
}: AssessmentSummaryProps) => {
  const { t } = useTranslation();

  const summaryData = assessmentSummary?.[sectionId]?.[fieldId];
  if (!summaryData) {
    return null;
  }

  const content = summaryData.summary!;
  if (!content) {
    return null;
  }

  return (
    <Message size="small">
      <MessageHeader
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Automated assessment assistance
        <Popup
          offset={[13, 2]}
          trigger={
            <Icon
              icon={CircleQuestionMarkIcon}
              size="small"
              style={{ color: "#666" }}
            />
          }
        >{`This is an automated summary for this question based on the ${t("generic.idea")} content. It does not represent a score or critique.`}</Popup>
      </MessageHeader>
      <Divider fitted style={{ marginTop: 4, marginBottom: 4 }} />

      <MarkdownViewer value={`"${content}"`} />
    </Message>
  );
};

const WordLimitContainer = styled.div<{ $exceedingWordLimit: boolean }>`
  margin-top: 5px;
  p {
    margin: 0;
    font-size: 10px;
    font-weight: ${({ $exceedingWordLimit }) =>
      $exceedingWordLimit ? 700 : 400};
    color: ${({ $exceedingWordLimit }) =>
      $exceedingWordLimit ? "red" : "black"};
  }
`;

type IdeaCustomFieldProps = {
  field: NonNullable<
    NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number]["fields"]
  >[number];
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number];
  updateIdea: (value: any, path: string[], replace?: boolean) => void;
  setFieldRef?: (ref: HTMLDivElement | null, fieldId: string) => void;
  isEditing?: boolean;
  externalChanges?: IdeaExternalChanges;
};

const IdeaCustomField = ({
  field,
  section,
  isEditing,
  updateIdea,
  externalChanges,
  setFieldRef,
}: IdeaCustomFieldProps) => {
  const { t } = useTranslation();
  const [idea] = useChildIdea<unknown, IdeaCustomFieldIdea>({
    select: (data): IdeaCustomFieldIdea => {
      const wordCountInfo = data?.submissibilityStatus?.wordCounts?.find(
        (w) => w.field === field.id,
      );
      return {
        _id: data._id,
        assessmentSummaries: data.assessmentSummaries,
        challenge: data.challenge,
        ownerChallenge: data.ownerChallenge,
        assessments: data.assessments,
        ownerIdeaTemplate: data.ownerIdeaTemplate,
        submissionDeadline: data.submissionDeadline,
        wordCountInfo,
        templated: {
          [field.id]: data.templated?.[field.id],
          ...Object.fromEntries(
            field.visibilityConditions?.conditions?.map((vc) => [
              vc.field,
              data.templated?.[vc.field],
            ]) || [],
          ),
        } as IdeaCustomFieldIdea["templated"],
      } as IdeaCustomFieldIdea;
    },
  });

  const ideaId = idea?._id;
  const challengeId = idea?.challenge;

  const user = useCurrentUser();

  const {
    isAssessingSelectedRound,
    selectedAssessmentRoundId,
    urlAssessmentRoundId,
    assessmentState,
    updateAssessment,
  } = useAssessmentEditor(idea);
  const isAssessingSection =
    section.type === "assessment" && isAssessingSelectedRound;
  const assessmentRound = useMemo(() => {
    return idea?.ownerIdeaTemplate?.assessments?.rounds?.find(
      (ar) => ar.id === assessmentState?.assessmentRoundId,
    );
  }, [assessmentState, idea]);

  /*
    Find an external change from collaboration users
  */
  const externalChange = useMemo(
    () => externalChanges?.templated?.[field.id],
    [externalChanges, field.id],
  );

  const fieldValue = useMemo(() => {
    if (isAssessingSection) {
      if (field.type === "assessment") {
        return assessmentState?.assessment?.[field.id]?.score;
      }

      return assessmentState?.assessment?.[field.id]?.value;
    }
    return idea?.templated?.[field.id];
  }, [idea, field, isAssessingSection, assessmentState]);

  /*
    Word counts are calculated server side as they use a more complicated approach to remove whitespace, punctuation etc. These are returned to the client on each save
  */
  const wordCount = idea?.wordCountInfo?.count || 0;
  const titleId = useId();

  const handleAssessmentUpdate = (value: any, path: string[]) => {
    // By default this is "templated" when updateIdea is called, but in assessments we want to update the current `assessment` value.
    path[0] = "assessment";

    // If the path is only 2 items long, we need to add the "value" key to the path so that the resulting object is { assessment: { [field.id]: { value: value } } } which leaves room in the object to add other keys like assessor "notes"
    if (path.length === 2) {
      path.push("value");
    }
    updateAssessment(value, path);
  };

  const handleFieldUpdate = isAssessingSection
    ? handleAssessmentUpdate
    : updateIdea;

  const assessmentHelperEnabled =
    !!idea?.ownerChallenge?.aiAssessmentHelper &&
    hasPermission(user!, "super.viewDashboard");

  const isVisible = fieldVisibilityCheck(idea, field);
  if (!isVisible) {
    return null;
  }

  if (field.type === "infotext") {
    return (
      <BodyContainer ref={setFieldRef && ((ref) => setFieldRef(ref, field.id))}>
        {isEditing || isAssessingSection ? <InfoText field={field} /> : null}
      </BodyContainer>
    );
  }

  if (field.type === "row") {
    return (
      <BodyContainer ref={setFieldRef && ((ref) => setFieldRef(ref, field.id))}>
        <BodyContainer />
      </BodyContainer>
    );
  }

  const canBypassDeadlineFieldLock =
    hasPermission(user!, "challenge.editIdeas", challengeId) ||
    hasPermission(
      user!,
      "org.manageChallenges",
      idea?.ownerChallenge?.organisation,
    );

  const fieldIsEditableUnderDeadline = isFieldEditableUnderDeadline(
    idea,
    field.id,
    { bypass: canBypassDeadlineFieldLock || isAssessingSection },
  );

  const fieldProps: FieldProps = {
    field: field,
    ideaId: ideaId!,
    challengeId: challengeId!,
    fieldValue: fieldValue,
    isEditing:
      (isEditing || (isAssessingSection && !assessmentState?.isSubmitted)) &&
      fieldIsEditableUnderDeadline,
    updateIdea: handleFieldUpdate,
    assessmentIsClosed: !!assessmentRound?.assessmentClosed,
    isAssessment: isAssessingSection,
  };

  let fieldComponent = null;
  if (field.type === "shorttext") {
    fieldComponent = <ShortText {...fieldProps} />;
  } else if (field.type === "longtext") {
    fieldComponent = <LongText {...fieldProps} />;
  } else if (field.type === "assessmentFeedback") {
    fieldComponent = <AssessmentFeedback {...fieldProps} />;
  } else if (field.type === "richtext") {
    fieldComponent = <RichText {...fieldProps} />;
  } else if (field.type === "dropdown") {
    fieldComponent = <Dropdown {...fieldProps} />;
  } else if (field.type === "slider") {
    fieldComponent = <Slider {...fieldProps} />;
  } else if (field.type === "optionMultiple") {
    fieldComponent = <OptionMultiple {...fieldProps} />;
  } else if (field.type === "image") {
    fieldComponent = <Image {...fieldProps} />;
  } else if (field.type === "video") {
    fieldComponent = <Video {...fieldProps} />;
  } else if (field.type === "file") {
    fieldComponent = <File {...fieldProps} />;
  } else if (field.type === "multiimage") {
    fieldComponent = <MultiImage {...fieldProps} />;
  } else if (field.type === "multifile") {
    fieldComponent = <MultiFile {...fieldProps} />;
  } else if (field.type === "creditsafereport") {
    fieldComponent = <CompanyReport {...fieldProps} />;
  } else if (isAssessingSelectedRound && field.type === "assessment") {
    fieldComponent = <Assessment {...fieldProps} />;
  } else if (field.type === "numerictext") {
    fieldComponent = <NumericalText {...fieldProps} />;
  } else if (field.type === "table") {
    fieldComponent = <TableField {...fieldProps} />;
  }

  const editingOrAssessing =
    isEditing ||
    (section.type === "assessment" &&
      (isAssessingSection ||
        (!isAssessingSelectedRound && !!urlAssessmentRoundId)));

  return (
    <BodyContainer
      ref={setFieldRef && ((ref) => setFieldRef(ref, field.id))}
      data-testid={`idea-field-${field.id}`}
      className={`field-${field.type}`}
      style={{ borderRadius: 5 }}
    >
      <FieldHeader
        ideaId={ideaId}
        titleId={titleId}
        field={field}
        updateIdea={updateIdea}
        externalChange={externalChange}
        showDescription={editingOrAssessing}
        showRequiredStatus={isEditing || !!isAssessingSection}
        showImportContent={isEditing}
        importingContentEnabled={
          !idea?.ownerChallenge?.preventIdeaContentImport
        }
      />
      <div
        style={{
          borderLeft: !editingOrAssessing ? "3px solid #cccccc" : undefined,
          paddingLeft: !editingOrAssessing ? 15 : undefined,
          marginLeft: !editingOrAssessing ? 5 : undefined,
        }}
      >
        <>
          {assessmentHelperEnabled ? (
            <AssessmentSummary
              assessmentSummary={
                idea?.assessmentSummaries?.[selectedAssessmentRoundId]
              }
              sectionId={section.id}
              fieldId={field.id}
            />
          ) : null}
          <FormFieldLabelledBy labelId={titleId}>
            {fieldComponent}
          </FormFieldLabelledBy>
          {isAssessingSection && field.type !== "assessmentFeedback" ? (
            <FormField disabled={!!assessmentRound?.assessmentClosed}>
              <Divider hidden />
              {assessmentState?.isSubmitted ? (
                <p>
                  <H5>Justification or notes</H5>
                  {assessmentState?.assessment?.[field.id]?.notes ?? ""}
                </p>
              ) : (
                <>
                  <FormLabel>Justification or notes</FormLabel>
                  <TextArea
                    key={ideaId}
                    style={{ minHeight: 115 }}
                    placeholder="Add context and reasoning to your evaluation..."
                    defaultValue={
                      assessmentState?.assessment?.[field.id]?.notes ?? ""
                    }
                    onChange={(_e, { value }) =>
                      handleFieldUpdate(value, [
                        "assessment",
                        field.id,
                        "notes",
                      ])
                    }
                  />
                </>
              )}
            </FormField>
          ) : null}
        </>
      </div>

      {field.wordLimit && isEditing ? (
        <WordLimitContainer
          $exceedingWordLimit={field.wordLimit - wordCount < 0}
        >
          <p>
            {t("ideas.actions.wordsRemaining", {
              count: field.wordLimit - wordCount,
            })}
          </p>
        </WordLimitContainer>
      ) : null}
    </BodyContainer>
  );
};

const IdeaCustomSection = (props: Omit<IdeaCustomFieldProps, "field">) => {
  const { section } = props;

  return (
    <Grid mobile={1} data-testid={`idea-section-${section.id}`}>
      {section.fields?.map((field) => (
        <IdeaCustomField key={field.id} field={field} {...props} />
      ))}
    </Grid>
  );
};

export default IdeaCustomSection;
